import type { ReactNode } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { SETTINGS } from './tokens';

export const SAVE_FAILED_MESSAGE = 'Couldn’t save. Check your connection and try again.';

export function SettingsCard({ children, labelledBy }: { children: ReactNode; labelledBy: string }) {
  return (
    <Box
      component="section"
      aria-labelledby={labelledBy}
      sx={{
        bgcolor: SETTINGS.cardBg,
        borderRadius: '12px',
        boxShadow: SETTINGS.cardShadow,
        p: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
      }}
    >
      {children}
    </Box>
  );
}

export function CardHeading({ id, children }: { id: string; children: ReactNode }) {
  return (
    <Typography id={id} component="h2" sx={{ fontFamily: SETTINGS.font, fontSize: 20, lineHeight: '28px', fontWeight: 700, color: SETTINGS.ink }}>
      {children}
    </Typography>
  );
}

export function CardSubline({ children }: { children: ReactNode }) {
  return (
    <Typography sx={{ mt: '4px', fontFamily: SETTINGS.font, fontSize: 16, lineHeight: '24px', color: SETTINGS.muted }}>
      {children}
    </Typography>
  );
}

export function NotConfirmedPill() {
  return (
    <Box
      component="span"
      sx={{
        flexShrink: 0,
        px: '12px',
        py: '4px',
        borderRadius: '999px',
        bgcolor: SETTINGS.pill.bg,
        color: SETTINGS.pill.fg,
        fontFamily: SETTINGS.font,
        fontSize: 13,
        lineHeight: '20px',
        fontWeight: 600,
      }}
    >
      Not confirmed yet
    </Box>
  );
}

export function ConfirmedLine({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '12px', color: SETTINGS.ink }}>
      <CheckCircleOutlineIcon sx={{ fontSize: 20, color: SETTINGS.muted }} />
      <Typography sx={{ fontFamily: SETTINGS.font, fontSize: 16, lineHeight: '24px' }}>{children}</Typography>
    </Box>
  );
}

export function CardHeader({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
      <Box sx={{ minWidth: 0 }}>{children}</Box>
      {aside}
    </Box>
  );
}

export function RowsFrame({ children }: { children: ReactNode }) {
  return <Box sx={{ border: `1px solid ${SETTINGS.divider}`, borderRadius: '8px', overflow: 'hidden' }}>{children}</Box>;
}

export function GroupLabel({ children }: { children: ReactNode }) {
  return (
    <Typography
      sx={{
        px: '16px',
        pt: '12px',
        pb: '4px',
        fontFamily: SETTINGS.font,
        fontSize: 12,
        lineHeight: '16px',
        fontWeight: 700,
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        color: SETTINGS.muted,
      }}
    >
      {children}
    </Typography>
  );
}

export function Footnote({ children }: { children: ReactNode }) {
  return (
    <Typography sx={{ fontFamily: SETTINGS.font, fontSize: 14, lineHeight: '20px', color: SETTINGS.muted }}>{children}</Typography>
  );
}

export function Mark({ children }: { children: ReactNode }) {
  return (
    <Typography component="span" sx={{ fontFamily: SETTINGS.font, fontSize: 16, lineHeight: '24px', fontWeight: 700, color: SETTINGS.mark }}>
      {children}
    </Typography>
  );
}

export function Note({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px', p: '12px 16px', borderRadius: '8px', bgcolor: SETTINGS.noteBg }}>
      <InfoOutlinedIcon sx={{ fontSize: 20, mt: '2px', color: SETTINGS.muted }} />
      <Typography sx={{ fontFamily: SETTINGS.font, fontSize: 16, lineHeight: '24px', color: SETTINGS.ink }}>{children}</Typography>
    </Box>
  );
}

export function SaveFailed({ visible }: { visible: boolean }) {
  if (!visible) return null;
  return (
    <Typography role="alert" sx={{ fontFamily: SETTINGS.font, fontSize: 14, lineHeight: '20px', color: SETTINGS.error }}>
      {SAVE_FAILED_MESSAGE}
    </Typography>
  );
}

export function Loading() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: '32px' }}>
      <CircularProgress size={28} />
    </Box>
  );
}
